"use client";
import React from "react";
import Card from "@/components/primitives/card";
import TeamLegend from "@/components/team/legend";
import {
  GameStats,
  GameStatus,
  GameTeamStats,
  MlbTeamStats,
  NbaTeamStats,
  NflStats,
  NflTeamStats,
  NhlTeamStats,
  Sport,
  TeamBasic,
  TeamColor,
  TeamStats,
  TeamStatsResponse,
  Year,
} from "common";
import {
  StatComparison,
  createGameStatComparison,
  createMlbStatComparison,
  createNbaStatsComparison,
  createNflStatComparison,
  createNhlStatsComparison,
} from "./stat-comparison-data";
import StatComparisonChart from "./stat-comparison-chart";
import useFetch from "@/utils/fetch";
import NotFound from "@/components/primitives/not-found";
import Tabs from "@/components/primitives/tabs";

// LOOK UP STATS BY TEAM ID
export default function GameStatsComparison({
  data,
  sport,
  status,
  awayTeam,
  homeTeam,
  season
}: {
  data: TeamStatsResponse | GameStats;
  sport: Sport;
  status: GameStatus;
  awayTeam: TeamBasic & TeamColor;
  homeTeam: TeamBasic & TeamColor;
  season: number;
}) {
  const [tab, setTab] = React.useState<string>("0");
  const baseUrl: string = `${process.env.NEXT_PUBLIC_API_URL}/api/sports/${sport}/${season}/team-stats/`;
  const params: URLSearchParams = new URLSearchParams();
  params.append("homeKey", homeTeam.abbreviation);
  params.append("awayKey", awayTeam.abbreviation);
  const request: string = `${baseUrl}?${params}`;

  const {
    data: fetchedData,
    isLoading,
    isError,
  } = useFetch<GameTeamStats | null>(null, request, []);
  let statComparison: StatComparison[] = [];
  if (fetchedData) {
    console.log("stat comparison:", fetchedData);
    switch (status) {
      case "Scheduled":
      case "Postponed":
      case "Canceled":
      case "Delayed":
      case "NotNecessary":
        switch (sport) {
          case "nfl":
            statComparison = createNflStatComparison(
              fetchedData.away as NflTeamStats,
              fetchedData.home as NflTeamStats
            );
            break;
          case "nba":
            statComparison = createNbaStatsComparison(
              fetchedData.away as NbaTeamStats,
              fetchedData.home as NbaTeamStats,
              status
            );
            break;
          case "mlb":
            statComparison = createMlbStatComparison(
              fetchedData.away as MlbTeamStats,
              fetchedData.home as MlbTeamStats
            );
            break;
          case "nhl":
            statComparison = createNhlStatsComparison(
              fetchedData.away as NhlTeamStats,
              fetchedData.home as NhlTeamStats,
              status
            );
            break;
        }
        break;
      default:
        statComparison = createGameStatComparison(data as GameStats, sport);
        break;
    }

    console.log("nhl stat comparison:", statComparison);
    return (
      <Card
        className="!pb-8 !pt-4 !gap-0"
        header={{
          title: (
            <div className="flex items-center gap-1">
              {awayTeam.name}{" "}
              <span className="text-title-medium pt-1">vs.</span>{" "}
              {homeTeam.name}
              <span>Stats</span>
            </div>
          ),
          action: <TeamLegend awayTeam={awayTeam} homeTeam={homeTeam} />,
        }}
      >
        {sport === "mlb" ? (
          <div className="flex flex-col">
            <div className="mb-2 flex desktop-grid:justify-end">

            <Tabs
              variant="pill"
              label="Mlb Stat Category"
              defaultValue="0"
              onValueChange={(v) => setTab(v)}
              items={[
                {
                  label: "Batting",
                  value: "0",
                },
                {
                  label: "Pitching",
                  value: "1",
                },
              ]}
            />
            </div>

            {tab === "0" ? (
              <StatComparisonChart
                data={[statComparison[1]]}
                awayColor={awayTeam.primaryColor}
                homeColor={homeTeam.primaryColor}
              />
            ) : (
              <StatComparisonChart
                data={[statComparison[0]]}
                awayColor={awayTeam.primaryColor}
                homeColor={homeTeam.primaryColor}
              />
            )}
          </div>
        ) : (
          <StatComparisonChart
            data={statComparison}
            awayColor={awayTeam.primaryColor}
            homeColor={homeTeam.primaryColor}
          />
        )}
      </Card>
    );
  }

  return <NotFound label="No stats found" />;
}
